import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import { FloorplanViewer, FloorplanProvider } from '@amzn/ihm-floorplan-viewer';
import { BayData, DisplayMode } from '@amzn/ihm-floorplan-core';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Toggle from '@amzn/meridian/toggle';
import useDisplayModes from './displayModes';
import useTooltipFromDisplayModes from './useTooltipFromDisplayModes';
import DisplayModeSelector from './displayModeSelector';
import AgileURLParameterTypes from '../types/AgileURLParameterTypes';
import { FloorplanData } from './zoneMapDataType';
import LdpTranslationButton from './LdpTranslationButton';
import SelectionToolbar from './SelectionToolbar';
import { ModifierKeys } from '../hooks/useFixtureSelection';
import { ZoneCreationProperties } from '../types/zoneMapType';
import FloorplanSelectionBridge from './FloorplanSelectionBridge';

export interface ViewZoneComponentProps {
  floorplanData: FloorplanData[];
  fixtureIdToZoneDataMap: Record<
    string,
    {
      zoneName: string;
      zoneId: string;
      productTypes: string;
      isAgile: boolean;
      zoneNameKey: string;
    }
  >;
  enableClick: boolean;
  zoneNameKeyMap: Map<string, string>;

  // New props for selection support
  selectionEnabled?: boolean;
  activeZoneIdx?: number;
  tempSelection?: Set<string>;
  zones?: Map<number, ZoneCreationProperties>;
  onFixtureSelect?: (fixtureId: string, modifierKeys: ModifierKeys) => void;
  onRectangleSelect?: (fixtureIds: string[]) => void;
  onActiveZoneChange?: (zoneIdx: number) => void;
  canUndo?: boolean;
  onUndo?: () => void;
  onCommitSelection?: () => void;
  onClearSelection?: () => void;
}

const ViewZoneComponent = ({
  floorplanData,
  fixtureIdToZoneDataMap,
  enableClick,
  zoneNameKeyMap,
  selectionEnabled = false,
  activeZoneIdx = 0,
  tempSelection = new Set(),
  zones = new Map(),
  onFixtureSelect,
  onRectangleSelect,
  onActiveZoneChange,
  canUndo = false,
  onUndo,
  onCommitSelection,
  onClearSelection,
}: ViewZoneComponentProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const displayModes = useDisplayModes(fixtureIdToZoneDataMap, zoneNameKeyMap);
  const [displayMode, setDisplayMode] = useState<DisplayMode | null>(displayModes.defaultOption);
  const tooltip = useTooltipFromDisplayModes(displayModes.optionsList);
  const [zoomIn, setZoomIn] = useState(true);
  const getZoneIdByFixtureId = (
    mapping: Record<string, { zoneName: string; zoneId: string; isAgile: boolean }>,
    fixtureId?: string,
  ): { zoneId: string; isAgile: boolean } => {
    if (fixtureId && fixtureId in mapping) {
      return {
        zoneId: mapping[fixtureId].zoneId,
        isAgile: mapping[fixtureId].isAgile,
      };
    }
    return {
      zoneId: '',
      isAgile: false,
    };
  };
  const agileFixtureData = floorplanData.filter((fixture) => fixture.isAgile);

  const handleFixtureClick = (fixtureData: BayData, event?: MouseEvent) => {
    // If selection is enabled, handle fixture selection
    if (selectionEnabled && onFixtureSelect && fixtureData.id) {
      const modifierKeys: ModifierKeys = {
        shift: event?.shiftKey || false,
        ctrl: event?.ctrlKey || false,
        meta: event?.metaKey || false,
      };
      onFixtureSelect(fixtureData.id, modifierKeys);
      return;
    }

    // Otherwise, handle navigation (existing behavior)
    const { zoneId, isAgile } = getZoneIdByFixtureId(fixtureIdToZoneDataMap, fixtureData.id);
    console.log('Navigate with zoneId:', zoneId, 'isAgile: ', isAgile);
    if (isAgile) {
      navigate('/agileManagement', {
        state: {
          storeId: searchParams.get('storeId'),
          date: searchParams.get('date'),
          zoneId,
        } as AgileURLParameterTypes,
      });
    }
  };

  const handleFloorplanSelectionChange = (selectedFixtureIds: string[]) => {
    // When we get a selection change from FloorplanViewer (box/rectangle selection)
    // Only process if it's a box selection (multiple fixtures or explicit box drag)
    // Single clicks are handled by handleFixtureClick
    if (selectionEnabled && onRectangleSelect && selectedFixtureIds.length > 1) {
      onRectangleSelect(selectedFixtureIds);
    }
  };

  useEffect(() => {
    // when fixtureIdToZoneDataMap changes, reset zone displayMode
    const currentDisplayMode = displayModes.optionsList.find((mode) => mode.id === displayMode?.id) as DisplayMode;
    setDisplayMode(currentDisplayMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixtureIdToZoneDataMap]);

  return displayMode ? (
    <Column height="100%">
      {/* Selection Toolbar - only show when selection is enabled */}
      {selectionEnabled && onActiveZoneChange && (
        <SelectionToolbar
          activeZoneIdx={activeZoneIdx}
          zones={zones}
          onActiveZoneChange={onActiveZoneChange}
          disabled={false}
          canUndo={canUndo}
          onUndo={onUndo}
          tempSelectionCount={tempSelection.size}
          onCommitSelection={onCommitSelection}
          onClearSelection={onClearSelection}
        />
      )}

      <Row spacingInset="none" alignmentHorizontal="center" alignmentVertical="bottom" spacing="400">
        <DisplayModeSelector
          displayModes={displayModes.optionsList}
          selectedDisplayMode={displayMode}
          setSelectedDisplayMode={setDisplayMode}
        />
        <Toggle checked={zoomIn} onChange={setZoomIn}>
          Only agile fixtures
        </Toggle>
        {enableClick && <LdpTranslationButton fixtureIdToZoneDataMap={fixtureIdToZoneDataMap} />}
      </Row>
      <Row height="100%" widths={['62%', '38%']}>
        <div style={{ position: 'relative', height: '100%', width: '100%', display: 'flex', justifyContent: 'center' }}>
          <FloorplanProvider>
            {/* Bridge component to sync FloorplanViewer's internal selection with our state */}
            {selectionEnabled && <FloorplanSelectionBridge onSelectionChange={handleFloorplanSelectionChange} />}

            {/* Determine if we should enable fixture clicking (either for navigation or selection) */}
            {(enableClick || selectionEnabled) && zoomIn && (
              <FloorplanViewer
                fixtureData={agileFixtureData}
                displayMode={displayMode}
                fixtureTooltip={tooltip}
                select={selectionEnabled}
                onFixtureClick={handleFixtureClick}
              />
            )}
            {(enableClick || selectionEnabled) && !zoomIn && (
              <FloorplanViewer
                fixtureData={floorplanData}
                displayMode={displayMode}
                fixtureTooltip={tooltip}
                select={selectionEnabled}
                onFixtureClick={handleFixtureClick}
              />
            )}
            {!enableClick && !selectionEnabled && zoomIn && (
              <FloorplanViewer fixtureData={agileFixtureData} displayMode={displayMode} fixtureTooltip={tooltip} />
            )}
            {!enableClick && !selectionEnabled && !zoomIn && (
              <FloorplanViewer fixtureData={floorplanData} displayMode={displayMode} fixtureTooltip={tooltip} />
            )}
          </FloorplanProvider>
        </div>
        <Column alignmentHorizontal="start">{displayMode.legend !== undefined && displayMode.legend}</Column>
      </Row>
    </Column>
  ) : null;
};

export default ViewZoneComponent;
